import { AuthContext } from '@/contexts/AuthContext';
import { useContext, useCallback, useEffect, useState } from 'react';
import { createLogger } from '@/utils/createLogger';

const logger = createLogger('useAuth');

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// JWT utility functions
export function useJWT() {
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('accessToken'),
  );

  // Store access token in localStorage
  const storeAccessToken = useCallback((token: string) => {
    localStorage.setItem('accessToken', token);
    setAccessToken(token);
  }, []);

  // Clear access token from localStorage
  const clearAccessToken = useCallback(() => {
    localStorage.removeItem('accessToken');
    setAccessToken(null);
  }, []);

  // Check if access token is expired
  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }, []);

  // Refresh access token using httpOnly cookie
  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        credentials: 'include', // Include httpOnly cookies
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.accessToken) {
          storeAccessToken(data.accessToken);
          return data.accessToken;
        }
      }

      // Refresh failed, clear tokens
      clearAccessToken();
      return null;
    } catch (error) {
      logger.error('Token refresh failed:', error);
      clearAccessToken();
      return null;
    }
  }, [storeAccessToken, clearAccessToken]);

  // Get valid access token (refresh if needed)
  const getValidAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accessToken) {
      return null;
    }

    if (isTokenExpired(accessToken)) {
      logger.debug('Access token expired, refreshing...');
      return await refreshToken();
    }

    return accessToken;
  }, [accessToken, isTokenExpired, refreshToken]);

  // Handle Google OAuth success
  const handleGoogleAuthSuccess = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('accessToken');

    if (accessToken) {
      storeAccessToken(accessToken);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    }

    return false;
  }, [storeAccessToken]);

  // Check for Google OAuth success on mount
  useEffect(() => {
    handleGoogleAuthSuccess();
  }, [handleGoogleAuthSuccess]);

  return {
    accessToken,
    storeAccessToken,
    clearAccessToken,
    getValidAccessToken,
    refreshToken,
    handleGoogleAuthSuccess,
  };
}
